// Workspace.tsx
import React from "react";
import StarryBackground from "./StarryBackground";
import BottomTabBar from "./BottomTabBar";
import WindowFrame from "./WindowFrame";
import { useSidebar, SIDEBAR_WIDTH_PX } from "@/components/ui/sidebar";
import WorkspaceBlastShutter from "./WorkspaceBlastShutter";
import CustomCalculatorBuilder from "../tools/CustomCalcBuilder";
import { isDemoModeScoped } from "@/lib/demoMode";
import {
  WorkspaceContext,
  WorkspaceState,
  WorkspaceTab,
  TabType,
  DropZone,
  Rect,
  WindowDragState,
} from "./workspace-context";
import ChatView from "@/components/chat/ChatView";
import NotesPanel from "@/components/tools/Notes/NotesPanel";
import TasksPanel from "@/components/tools/Tasks/TasksPanel";
import PlumbingCalcPanel from "@/components/tools/PlumbingCalcPanel";
import ProjectTrackerPanel from "@/components/tools/ProjectTrackerPanel";
import OwnerRequirementsPanel from "@/components/tools/OwnerRequirementsPanel";
import { useUser } from "@clerk/clerk-react";
import CalceraBallPanel from "@/components/easteregg/CalceraBallPanel";
import TimesheetPanel from "@/components/tools/Timesheet/TimesheetPanel";


const STORAGE_KEY = "workspace:layout:v4";
const EDGE_THRESHOLD = 96;
const SNAP_GAP = 8;
const BOTTOM_BAR_HEIGHT = 40; // must match BottomTabBar height (h-10)
// remove this if you want to rely on SIDEBAR_WIDTH_PX only
// const SIDEBAR_WIDTH = 260;



function bumpActivityPair(
  tabs: WorkspaceTab[],
  activeId: string | null,
  victimId: string | null,
): WorkspaceTab[] {
  if (!activeId) return tabs;
  const now = Date.now();
  const activeTime = now;
  const victimTime = now - 1;
  return tabs.map((t) => {
    if (t.id === activeId) {
      return { ...t, lastActiveAt: activeTime };
    }
    if (victimId && t.id === victimId) {
      return { ...t, lastActiveAt: victimTime };
    }
    return t;
  });
}

function newId(type: TabType): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `tab-${type}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function tabTitle(type: TabType, index: number): string {
  switch (type) {
    case "chat":
      return index <= 1 ? "Chat" : `Chat ${index}`;
    case "notes":
      return "Notes";
    case "tasks":
      return "Tasks";
    case "custom":
      return "Custom";
    case "tracker":
      return "Project Tracker";
    case "owner-requirements":
      return "Owner Requirements";
    case "timesheet":
      return "Timesheet";
    case "calcera-ball":
      return "Calcera Ball";
    default:
      return "Tab";
  }
}

function createTabInstance(
  type: TabType,
  counters: Record<TabType, number>,
  tile: Exclude<DropZone, "tab-bar">,
  opts?: {
    projectId?: string | null;
    projectName?: string | null;
    clientName?: string | null;
    isMiscProject?: boolean;
    calculatorId?: string | null;
    calculatorName?: string | null;
  },
): { tab: WorkspaceTab; counters: Record<TabType, number> } {
  const nextCounters: Record<TabType, number> = { ...counters };
  const index = (nextCounters[type] ?? 0) + 1;
  nextCounters[type] = index;

  const baseTitle = tabTitle(type, index);

  const title =
    opts?.projectName && (type === "notes" || type === "tasks" || type === "tracker" || type === "owner-requirements")
      ? `${baseTitle} - ${opts.projectName}`
      : baseTitle;

  return {
    tab: {
      id: newId(type),
      type,
      title,
      dirty: false,
      tilePosition: tile,
      projectId: opts?.projectId ?? null,
      projectName: opts?.projectName ?? null,
      clientName: opts?.clientName ?? null,
      isMiscProject: opts?.isMiscProject ?? false,

      lastActiveAt: Date.now(),
    },
    counters: nextCounters,
  };
}

function computeTileRects(
  bounds: DOMRect,
  sidebarOffset: number,
): Partial<Record<Exclude<DropZone, "tab-bar">, Rect>> {
  const contentTop = 0;
  const contentHeight = Math.max(0, bounds.height - BOTTOM_BAR_HEIGHT);
  const contentWidth = Math.max(0, bounds.width - sidebarOffset);
  const gap = SNAP_GAP;
  const halfWidth = Math.max(0, Math.floor(contentWidth / 2));
  const halfHeight = Math.max(0, Math.floor(contentHeight / 2));

  const inset = (x: number, y: number, w: number, h: number): Rect => ({
    x: sidebarOffset + x + gap / 2,
    y: y + gap / 2 + contentTop,
    width: Math.max(0, w - gap),
    height: Math.max(0, h - gap),
  });

  const r: Partial<Record<Exclude<DropZone, "tab-bar">, Rect>> = {};

  // center zone - 50% of width and height centered
  const centerWidth = contentWidth * 0.5;
  const centerHeight = contentHeight * 0.5;
  const centerX = (contentWidth - centerWidth) / 2;
  const centerY = (contentHeight - centerHeight) / 2;
  r["center"] = inset(centerX, centerY, centerWidth, centerHeight);

  r["fullscreen"] = inset(0, 0, contentWidth, contentHeight);
  r["left-half"] = inset(0, 0, halfWidth, contentHeight);
  r["right-half"] = inset(halfWidth, 0, contentWidth - halfWidth, contentHeight);
  r["top-half"] = inset(0, 0, contentWidth, halfHeight);
  r["bottom-half"] = inset(0, halfHeight, contentWidth, contentHeight - halfHeight);
  r["top-left"] = inset(0, 0, halfWidth, halfHeight);
  r["top-right"] = inset(halfWidth, 0, contentWidth - halfWidth, halfHeight);
  r["bottom-left"] = inset(0, halfHeight, halfWidth, contentHeight - halfHeight);
  r["bottom-right"] = inset(halfWidth, halfHeight, contentWidth - halfWidth, contentHeight - halfHeight);
  return r;
}

function rectsOverlap(a: Rect, b: Rect): boolean {
  return !(
    a.x + a.width <= b.x ||
    b.x + b.width <= a.x ||
    a.y + a.height <= b.y ||
    b.y + b.height <= a.y
  );
}

const SPAWN_TILE_PRIORITY: Exclude<DropZone, "tab-bar">[] = [
  "left-half",
  "right-half",
  "top-half",
  "bottom-half",
  "top-left",
  "top-right",
  "bottom-left",
  "bottom-right",
];

function chooseSpawnTile(
  tabs: WorkspaceTab[],
  tileRects: Partial<Record<Exclude<DropZone, "tab-bar">, Rect>>,
): Exclude<DropZone, "tab-bar"> | null {
  const visibleTabs = tabs.filter((t) => !t.isMinimized);
  if (visibleTabs.length === 0) {
    // Empty workspace should default to fullscreen fallback behavior.
    return null;
  }

  const occupiedRects = visibleTabs
    .map((t) => tileRects[t.tilePosition])
    .filter((r): r is Rect => Boolean(r));

  for (const tile of SPAWN_TILE_PRIORITY) {
    const candidateRect = tileRects[tile];
    if (!candidateRect) continue;
    const blocked = occupiedRects.some((occupied) => rectsOverlap(candidateRect, occupied));
    if (!blocked) return tile;
  }

  return null;
}

function computeDropZone(
  bounds: DOMRect,
  point: { x: number; y: number },
  sidebarOffset: number,
): DropZone | null {
  if (Number.isNaN(point.x) || Number.isNaN(point.y)) return null;
  if (
    point.x < bounds.left ||
    point.x > bounds.right ||
    point.y < bounds.top ||
    point.y > bounds.bottom
  ) {
    return null;
  }

  // raw mouse position relative to the workspace
  const rawX = point.x - bounds.left;
  const relativeY = point.y - bounds.top;

  const contentWidth = Math.max(0, bounds.width - sidebarOffset);
  const contentHeight = bounds.height - BOTTOM_BAR_HEIGHT;

  // shift X so 0 = left edge of the grid (right of sidebar)
  const relativeX = rawX - sidebarOffset;

  // if we’re over the sidebar area or outside the grid, no drop zone
  if (relativeX < 0 || relativeX > contentWidth || relativeY < 0 || relativeY > contentHeight) {
    return null;
  }

  const nearLeft = relativeX <= EDGE_THRESHOLD;
  const nearRight = relativeX >= contentWidth - EDGE_THRESHOLD;
  const nearTop = relativeY <= EDGE_THRESHOLD;
  const nearBottom = relativeY >= contentHeight - EDGE_THRESHOLD;

  if (nearLeft && nearTop) return "top-left";
  if (nearRight && nearTop) return "top-right";
  if (nearLeft && nearBottom) return "bottom-left";
  if (nearRight && nearBottom) return "bottom-right";
  if (nearLeft) return "left-half";
  if (nearRight) return "right-half";
  if (nearTop) return "top-half";
  if (nearBottom) return "bottom-half";
  return "center";
}

function computePreviewRect(
  bounds: DOMRect,
  zone: DropZone | null,
  sidebarOffset: number,
): Rect | null {
  if (!zone || zone === "tab-bar") {
    return null;
  }
  const tiles = computeTileRects(bounds, sidebarOffset);
  return tiles[zone] ?? null;
}

function loadState(): WorkspaceState | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") return null;
      const tabs: WorkspaceTab[] = Array.isArray(parsed.tabs) ? parsed.tabs : [];
      const counters: Record<TabType, number> =
        typeof parsed.counters === "object" && parsed.counters
          ? parsed.counters
          : {
              chat: 0,
              notes: 0,
              tasks: 0,
              plumbing: 0,
              mechanical: 0,
              electrical: 0,
              fire: 0,
              technology: 0,
              tracker: 0,
              "calcera-ball": 0,
            };
      return {
        tabs,
        counters,
        tileRects: {},
        activeTabId: parsed.activeTabId,
        windowDragState: null,
      };
    }

    const legacyRaw = window.localStorage.getItem("workspace:layout");
    if (legacyRaw) {
      const legacy = JSON.parse(legacyRaw);
      if (
        legacy &&
        typeof legacy === "object" &&
        legacy.panes &&
        typeof legacy.panes === "object"
      ) {
        const paneToTile: Record<string, Exclude<DropZone, "tab-bar">> = {
          single: "fullscreen",
          left: "left-half",
          right: "right-half",
          top: "top-half",
          bottom: "bottom-half",
          topLeft: "top-left",
          topRight: "top-right",
          bottomLeft: "bottom-left",
          bottomRight: "bottom-right",
        };
        const counters: Record<TabType, number> = {
          chat: 0,
          notes: 0,
          tasks: 0,
          plumbing: 0,
          mechanical: 0,
          electrical: 0,
          fire: 0,
          technology: 0,
          tracker: 0,
          custom: 0,
          ...(legacy.counters || {}),
        };
        const tabs: WorkspaceTab[] = [];
        for (const [paneKey, paneVal] of Object.entries(legacy.panes)) {
          const tile = paneToTile[paneKey] || "fullscreen";
          if (!paneVal || !Array.isArray((paneVal as any).tabs)) continue;
          for (const t of (paneVal as any).tabs) {
            if (!t || typeof t !== "object") continue;
            const type: TabType | undefined = [
              "chat",
              "notes",
              "tasks",
              "plumbing",
              "mechanical",
              "electrical",
              "fire",
              "technology",
              "tracker",
              "timesheet",
              "calcera-ball",
            ].includes((t as any).type)
              ? (t as any).type
              : undefined;
            if (!type) continue;
            const id = typeof (t as any).id === "string" ? (t as any).id : newId(type);
            const title =
              typeof (t as any).title === "string"
                ? (t as any).title
                : tabTitle(type, (counters[type] ?? 0) + 1);
            counters[type] = (counters[type] ?? 0) + 1;
            tabs.push({
              id,
              type,
              title,
              dirty: Boolean((t as any).dirty),
              tilePosition: tile,
            });
          }
        }
        const activeTabId = (() => {
          const activePaneId = legacy.activePaneId;
          const activeId = legacy.panes?.[activePaneId]?.activeId;
          return typeof activeId === "string" ? activeId : tabs[0]?.id;
        })();
        return { tabs, counters, tileRects: {}, activeTabId, windowDragState: null };
      }
    }

    return null;
  } catch {
    return null;
  }
}

function saveState(state: WorkspaceState) {
  if (typeof window === "undefined") return;
  try {
    const serializable: WorkspaceState = {
      ...state,
      tileRects: {},
      windowDragState: null,
    };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(serializable));
  } catch {
    // ignore storage errors
  }
}

const DEFAULT_COUNTERS: Record<TabType, number> = {
  chat: 0,
  notes: 0,
  tasks: 0,
  custom: 0,
  tracker: 0,
  "owner-requirements": 0,
  timesheet: 0,
  "calcera-ball": 0,
};

function defaultWorkspaceState(
  bounds: DOMRect | null,
  sidebarOffset: number,
  isDemo: boolean,
): WorkspaceState {
  // Demo mode (entered via Playground button) should start with nothing open.
  if (isDemo) {
    return {
      tabs: [],
      counters: { ...DEFAULT_COUNTERS },
      tileRects: bounds ? computeTileRects(bounds, sidebarOffset) : {},
      activeTabId: undefined,
      windowDragState: null,
    };
  }
  const counters = { ...DEFAULT_COUNTERS };
  const { tab, counters: updatedCounters } = createTabInstance("chat", counters, "fullscreen");
  return {
    tabs: [tab],
    counters: updatedCounters,
    tileRects: bounds ? computeTileRects(bounds, sidebarOffset) : {},
    activeTabId: tab.id,
    windowDragState: null,
  };
}

function tabById(state: WorkspaceState, id: string): number {
  return state.tabs.findIndex((t) => t.id === id);
}

//**************************************************************************
//**************************************************************************
//**************************************************************************
//**************************************************************************

export default function Workspace() {
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const workspaceRef = React.useRef<HTMLDivElement | null>(null);

  const { open: sidebarOpen } = useSidebar();
  const sidebarOffset = sidebarOpen ? SIDEBAR_WIDTH_PX : 0;

  const [state, setState] = React.useState<WorkspaceState>(() =>
    defaultWorkspaceState(null, 0, false),
  );

  const [minimizedTabIds, setMinimizedTabIds] = React.useState<Set<string>>(
    () => new Set(),
  );
  const { user, isLoaded: userLoaded, isSignedIn } = useUser();

  const isDemo = React.useMemo(() => {
    if (!userLoaded || !isSignedIn) return false;
    return isDemoModeScoped({
      userId: user?.id,
      email: user?.primaryEmailAddress?.emailAddress,
    });
  }, [userLoaded, isSignedIn, user?.id, user?.primaryEmailAddress?.emailAddress]);

  const showWorkspaceLock =
  userLoaded &&
  !isSignedIn;

  const userHeaders = React.useMemo(() => {
    if (!userLoaded) return undefined;
    if (!isSignedIn) return undefined;
    const id = user?.id;
    const email = user?.primaryEmailAddress?.emailAddress || undefined;
    return id ? { id, email } : undefined;
  }, [userLoaded, isSignedIn, user?.id, user?.primaryEmailAddress?.emailAddress]);

  const debugFrames = React.useMemo(() => {
    try {
      const qs =
        typeof window !== "undefined"
          ? new URLSearchParams(window.location.search)
          : null;
      const qsVal = qs?.get("debugFrames");
      return qsVal === "1" || qsVal === "true";
    } catch {
      return false;
    }
  }, []);
  const [workspaceBox, setWorkspaceBox] = React.useState<Rect | null>(null);

  const getToolsBounds = React.useCallback((): DOMRect | null => {
    if (!workspaceRef.current) return null;
    return workspaceRef.current.getBoundingClientRect();
  }, []);

  const reflowVisibleTabs = React.useCallback(
    (prevState: WorkspaceState): WorkspaceState => {
      return prevState;
    },
    [],
  );

  React.useEffect(() => {
    const bounds = getToolsBounds();
    if (!bounds) return;

    setState((prev) => ({
      ...prev,
      tileRects: computeTileRects(bounds, sidebarOffset),
    }));
  }, [sidebarOffset, getToolsBounds]);

  React.useLayoutEffect(() => {
    const bounds = getToolsBounds();
    const loaded = loadState();

    if (loaded) {
      const withRects: WorkspaceState = {
        ...loaded,
        tileRects: bounds ? computeTileRects(bounds, sidebarOffset) : {},
      };

      // enforce: 1 visible tab -> center, etc.
      setState(reflowVisibleTabs(withRects));
    } else {
      setState(defaultWorkspaceState(bounds, sidebarOffset, isDemo));
    }
  }, [getToolsBounds, reflowVisibleTabs, sidebarOffset, isDemo]);

  React.useEffect(() => {
    saveState(state);
  }, [state.tabs, state.activeTabId, state.counters]);

  React.useEffect(() => {
    if (!workspaceRef.current || typeof ResizeObserver === "undefined") return;
    const elem = workspaceRef.current;
    const ro = new ResizeObserver(() => {
      const rawBounds = elem.getBoundingClientRect();
      const toolsBounds = getToolsBounds();
      if (!toolsBounds) return;

      const rects = computeTileRects(toolsBounds, sidebarOffset);
      setState((prev) => ({ ...prev, tileRects: rects }));

      setWorkspaceBox({
        x: rawBounds.left,
        y: rawBounds.top,
        width: rawBounds.width,
        height: rawBounds.height,
      });
    });
    ro.observe(elem);
    return () => ro.disconnect();
  }, [getToolsBounds, sidebarOffset]);

  const isTabMinimized = React.useCallback(
    (tabId: string) => minimizedTabIds.has(tabId),
    [minimizedTabIds],
  );

  // ========================================
  // LOCK HELPERS
  // ========================================

  const isTabLocked = React.useCallback(
    (tabId: string) =>
      state.tabs.find((t) => t.id === tabId)?.isLocked ?? false,
    [state.tabs],
  );

const toggleTabLocked = React.useCallback((tabId: string) => {
  setState((prev) => ({
    ...prev,
    tabs: prev.tabs.map((t) =>
      t.id === tabId ? { ...t, isLocked: !t.isLocked } : t
    ),
  }));
}, []);


  const setTabMinimized = React.useCallback(
    (tabId: string, minimized: boolean) => {
      if (isTabLocked(tabId)) return;

      if (minimized) {
        setMinimizedTabIds((prev) => {
          const next = new Set(prev);
          next.add(tabId);
          return next;
        });
        setState((prev) => {
          const idx = tabById(prev, tabId);
          if (idx === -1) return prev;
          const t = prev.tabs[idx];
          const nextTabs = [...prev.tabs];
          nextTabs[idx] = {
            ...t,
            isMinimized: true,
            preMinimizeTile: t.tilePosition,
          };
          return { ...prev, tabs: nextTabs };
        });
        return;
      }

      // --- Restore ---
      setState((prev) => {
        const idx = tabById(prev, tabId);
        if (idx === -1) return prev;
        const current = prev.tabs[idx];
        if (!current.isMinimized) return prev;

        const restoredTile = current.preMinimizeTile ?? "fullscreen";

        const bounds = getToolsBounds();
        const tiles = bounds
          ? computeTileRects(bounds, sidebarOffset)
          : prev.tileRects;

        const restoredRect = tiles?.[restoredTile];

        let nextTabs = prev.tabs.map((t) =>
          t.id === tabId
            ? { ...t, isMinimized: false, tilePosition: restoredTile }
            : t,
        );

        const displacedIds = new Set<string>();

        if (restoredRect && tiles) {
          const relocateZones: Exclude<DropZone, "tab-bar">[] = [
            "left-half",
            "right-half",
            "top-half",
            "bottom-half",
            "top-left",
            "top-right",
            "bottom-left",
            "bottom-right",
            "center",
          ];

          const visibleOthers = nextTabs.filter(
            (t) => !t.isMinimized && t.id !== tabId,
          );

          for (const other of visibleOthers) {
            const otherRect = tiles[other.tilePosition];
            if (!otherRect || !rectsOverlap(restoredRect, otherRect)) continue;

            const stayingRects = nextTabs
              .filter(
                (t) =>
                  !t.isMinimized &&
                  t.id !== other.id &&
                  !displacedIds.has(t.id),
              )
              .map((t) => tiles[t.tilePosition])
              .filter(Boolean) as Rect[];

            let relocated = false;
            for (const zone of relocateZones) {
              const zoneRect = tiles[zone];
              if (!zoneRect) continue;
              const conflicts = stayingRects.some((r) =>
                rectsOverlap(zoneRect, r),
              );
              if (!conflicts) {
                nextTabs = nextTabs.map((t) =>
                  t.id === other.id ? { ...t, tilePosition: zone } : t,
                );
                relocated = true;
                break;
              }
            }

            if (!relocated) {
              displacedIds.add(other.id);
              nextTabs = nextTabs.map((t) =>
                t.id === other.id
                  ? { ...t, isMinimized: true, preMinimizeTile: other.tilePosition }
                  : t,
              );
            }
          }
        }

        const bumpedTabs = bumpActivityPair(nextTabs, tabId, null);

        setMinimizedTabIds((prevSet) => {
          const s = new Set(prevSet);
          s.delete(tabId);
          for (const id of displacedIds) s.add(id);
          return s;
        });

        return { ...prev, tabs: bumpedTabs, activeTabId: tabId };
      });
    },
    [getToolsBounds, sidebarOffset, isTabLocked],
  );

  const setActiveTab = React.useCallback((tabId: string) => {
    setState((prev) => {
      const idx = tabById(prev, tabId);
      if (idx === -1) return prev;
      const nextTabs = prev.tabs.map((t, i) =>
        i === idx ? { ...t, lastActiveAt: Date.now() } : t,
      );
      return { ...prev, tabs: nextTabs, activeTabId: tabId };
    });
  }, []);

  const markTabDirty = React.useCallback((tabId: string, dirty: boolean) => {
    setState((prev) => {
      const idx = tabById(prev, tabId);
      if (idx === -1) return prev;
      const t = prev.tabs[idx];
      if (Boolean(t.dirty) === dirty) return prev;
      const nextTabs = [...prev.tabs];
      nextTabs[idx] = { ...t, dirty };
      return { ...prev, tabs: nextTabs };
    });
  }, []);

  const setTabTitle = React.useCallback((tabId: string, title: string) => {
    const trimmed = title.trim();
    setState((prev) => {
      const idx = tabById(prev, tabId);
      if (idx === -1) return prev;
      const current = prev.tabs[idx];
      if (current.title === trimmed) return prev;
      const nextTabs = [...prev.tabs];
      nextTabs[idx] = { ...current, title: trimmed };
      return { ...prev, tabs: nextTabs };
    });
  }, []);

  const setTabScope = React.useCallback(
    (
      tabId: string,
      scope: { projectId?: string | null; projectName?: string | null; clientName?: string | null; isMiscProject?: boolean },
    ) => {
      setState((prev) => {
        const idx = tabById(prev, tabId);
        if (idx === -1) return prev;
        const current = prev.tabs[idx];
        const projectId = scope.projectId ?? null;
        const projectName = scope.projectName ?? null;
        const clientName = scope.clientName ?? null;
        const isMiscProject = scope.isMiscProject ?? false;

        const nextTitle = (() => {
          if (current.type === "notes") {
            if (projectId) return projectName ? `Notes - ${projectName}` : "Notes";
            if (isMiscProject) return "Notes - Misc";
            return "Notes";
          }
          if (current.type === "tasks") {
            if (projectId) return projectName ? `Tasks - ${projectName}` : "Tasks";
            if (isMiscProject) return "Tasks - Misc";
            return "Tasks";
          }
          if (current.type === "tracker") {
            if (projectId) return projectName ? `Project Tracker - ${projectName}` : "Project Tracker";
            return "Project Tracker";
          }
          if (current.type === "owner-requirements") {
            if (projectId) return projectName ? `Owner Requirements - ${projectName}` : "Owner Requirements";
            return "Owner Requirements";
          }
          return current.title;
        })();

        const nextTabs = [...prev.tabs];
        nextTabs[idx] = {
          ...current,
          title: nextTitle,
          projectId,
          projectName,
          clientName,
          isMiscProject,
        };
        return { ...prev, tabs: nextTabs };
      });
    },
    [],
  );

  const evaluateDropMeta = React.useCallback(
    (point: { x: number; y: number }) => {
      const bounds = getToolsBounds();
      if (!bounds) {
        return {
          dropZone: null as DropZone | null,
          previewRect: null as Rect | null,
        };
      }
      const rawZone = computeDropZone(bounds, point, sidebarOffset);
      const previewRect = computePreviewRect(bounds, rawZone, sidebarOffset);
      return { dropZone: rawZone, previewRect };
    },
    [getToolsBounds, sidebarOffset],
  );

  const openTab = React.useCallback(
  (
    type: TabType,
    options?: {
      forceNew?: boolean;
      projectId?: string | null;
      projectName?: string | null;
      clientName?: string | null;
      isMisc?: boolean;
      calculatorId?: string;
      calculatorName?: string;
    },
  ) => {
    const bounds = getToolsBounds();
    const tileRects = bounds ? computeTileRects(bounds, sidebarOffset) : null;

    setState((prev) => {
      const projectId = options?.projectId ?? null;
      const projectName = options?.projectName ?? null;
      const clientName = options?.clientName ?? null;
      const isMiscProject = options?.isMisc ?? false;
      const calculatorId = options?.calculatorId ?? null;
      const calculatorName = options?.calculatorName ?? null;

      // 1) Prefer focusing an existing matching visible tab
      if (!options?.forceNew) {
        const existing = prev.tabs.find((t) => {
          if (t.type !== type) return false;
          if (t.isMinimized) return false;

          if (type === "notes" || type === "tasks") {
            return (t.projectId ?? null) === projectId;
          }

          if (type === "tracker" || type === "owner-requirements") {
            return (t.projectId ?? null) === projectId;
          }

          return true;
        });

        if (existing) {
          const nextTabs = prev.tabs.map((t) =>
            t.id === existing.id ? { ...t, lastActiveAt: Date.now() } : t,
          );
          return { ...prev, tabs: nextTabs, activeTabId: existing.id };
        }

        // 2) If a tab of this type is already open, and it's a notes/tasks tab,
        // reuse it by updating its scope instead of creating a new instance.
        const shouldReuseExisting =
          (type === "notes" || type === "tasks" || type === "tracker" || type === "owner-requirements");

        if (shouldReuseExisting) {
          const visibleOfType = prev.tabs.filter((t) => t.type === type && !t.isMinimized);
          if (visibleOfType.length > 0) {
            const activeSameType = prev.tabs.find(
              (t) => t.id === prev.activeTabId && t.type === type && !t.isMinimized,
            );

            const target =
              activeSameType ??
              visibleOfType.reduce((best, t) => {
                const tLast = t.lastActiveAt ?? 0;
                const bLast = best.lastActiveAt ?? 0;
                return tLast > bLast ? t : best;
              }, visibleOfType[0]);

            const updatedTitle = (() => {
              if (type === "notes") {
                if (projectId) return projectName ? `Notes - ${projectName}` : "Notes";
                if (isMiscProject) return "Notes - Misc";
                return "Notes";
              }

              if (type === "tasks") {
                if (projectId) return projectName ? `Tasks - ${projectName}` : "Tasks";
                if (isMiscProject) return "Tasks - Misc";
                return "Tasks";
              }

              if (type === "tracker") {
                if (projectId) return projectName ? `Project Tracker - ${projectName}` : "Project Tracker";
                return "Project Tracker";
              }

              if (type === "owner-requirements") {
                if (projectId) return projectName ? `Owner Requirements - ${projectName}` : "Owner Requirements";
                return "Owner Requirements";
              }

              if (type === "custom") {
                if (calculatorName) return `Custom Calculator - ${calculatorName}`;
                return "Custom Calculator";
              }

              return target.title;
            })();

            const nextTabs = prev.tabs.map((t) =>
              t.id === target.id
                ? {
                    ...t,
                    title: updatedTitle,

                    projectId,
                    projectName,
                    clientName,
                    isMiscProject,

                    lastActiveAt: Date.now(),
                  }
                : t,
            );

            return { ...prev, tabs: nextTabs, activeTabId: target.id };
          }
        }

        // 3) If switching notes/tasks back to unscoped, try to reuse a scoped visible tab
        if (projectId === null && !isMiscProject && (type === "notes" || type === "tasks")) {
          const visibleOfType = prev.tabs.filter((t) => t.type === type && !t.isMinimized);
          const visibleScoped = visibleOfType.filter(
            (t) => (t.projectId ?? null) !== null || (t.isMiscProject ?? false),
          );

          if (visibleScoped.length > 0) {
            const activeScoped = prev.tabs.find(
              (t) =>
                t.id === prev.activeTabId &&
                t.type === type &&
                !t.isMinimized &&
                ((t.projectId ?? null) !== null || (t.isMiscProject ?? false)),
            );

            const target =
              activeScoped ??
              visibleScoped.reduce((best, t) => {
                const tLast = t.lastActiveAt ?? 0;
                const bLast = best.lastActiveAt ?? 0;
                return tLast > bLast ? t : best;
              }, visibleScoped[0]);

            const updatedTitle = type === "notes" ? "Notes" : "Tasks";

            const nextTabs = prev.tabs.map((t) =>
              t.id === target.id
                ? {
                    ...t,
                    title: updatedTitle,
                    projectId: null,
                    projectName: null,
                    isMiscProject: false,
                    lastActiveAt: Date.now(),
                  }
                : t,
            );

            return { ...prev, tabs: nextTabs, activeTabId: target.id };
          }
        }

        // 4) Restore minimized (same as your existing behavior)
        const allowRestoreMinimized =
          type === "chat" ||
          type === "notes" ||
          type === "tasks" ||
          type === "custom" ||
          type === "tracker" ||
          type === "owner-requirements";

        if (allowRestoreMinimized) {
          const minimizedCandidates = prev.tabs.filter((t) => {
            if (t.type !== type) return false;
            if (!t.isMinimized) return false;

            if (type === "notes" || type === "tasks") {
              return (t.projectId ?? null) === projectId;
            }

            if (type === "tracker" || type === "owner-requirements") {
              return (t.projectId ?? null) === projectId;
            }

            // default for other tools (unscoped)
            return true;
          });

          if (minimizedCandidates.length > 0) {
            let target = minimizedCandidates[0];
            for (const t of minimizedCandidates) {
              const tLast = t.lastActiveAt ?? 0;
              const curLast = target.lastActiveAt ?? 0;
              if (tLast > curLast) target = t;
            }

            const updatedTitle = (() => {
              if (type === "notes") {
                if (projectId) return projectName ? `Notes - ${projectName}` : "Notes";
                if (isMiscProject) return "Notes - Misc";
                return "Notes";
              }

              if (type === "tasks") {
                if (projectId) return projectName ? `Tasks - ${projectName}` : "Tasks";
                if (isMiscProject) return "Tasks - Misc";
                return "Tasks";
              }

              if (type === "tracker") {
                if (projectId) return projectName ? `Project Tracker - ${projectName}` : "Project Tracker";
                return "Project Tracker";
              }

              if (type === "owner-requirements") {
                if (projectId) return projectName ? `Owner Requirements - ${projectName}` : "Owner Requirements";
                return "Owner Requirements";
              }

              if (type === "custom") {
                if (calculatorName) return `Custom Calculator - ${calculatorName}`;
                return "Custom Calculator";
              }

              return target.title;
            })();

            const tabId = target.id;
            const nextTabs = prev.tabs.map((t) =>
              t.id === tabId
                ? {
                    ...t,
                    title: updatedTitle,
                    projectId,
                    projectName,
                    clientName,
                    isMiscProject,
                    lastActiveAt: Date.now(),
                  }
                : t,
            );
            setTimeout(() => setTabMinimized(tabId, false), 0);
            return { ...prev, tabs: nextTabs, activeTabId: tabId };
          }

          if (type === "owner-requirements") {
            const minimizedOfType = prev.tabs.filter((t) => t.type === type && t.isMinimized);
            if (minimizedOfType.length > 0) {
              const target = minimizedOfType.reduce((best, t) => {
                const tLast = t.lastActiveAt ?? 0;
                const bLast = best.lastActiveAt ?? 0;
                return tLast > bLast ? t : best;
              }, minimizedOfType[0]);

              const updatedTitle =
                projectId && projectName ? `Owner Requirements - ${projectName}` : "Owner Requirements";

              const tabId = target.id;
              const nextTabs = prev.tabs.map((t) =>
                t.id === tabId
                  ? {
                      ...t,
                      title: updatedTitle,
                      projectId,
                      projectName,
                      clientName,
                      isMiscProject,
                      lastActiveAt: Date.now(),
                    }
                  : t,
              );

              setTimeout(() => setTabMinimized(tabId, false), 0);
              return { ...prev, tabs: nextTabs, activeTabId: tabId };
            }
          }
        }
      }

      // 5) Create new tab in the largest available pane first.
      const spawnTile = tileRects ? chooseSpawnTile(prev.tabs, tileRects) : null;
      if (spawnTile) {
        const { tab, counters } = createTabInstance(type, prev.counters, spawnTile, {
          projectId,
          projectName,
          clientName,
          isMiscProject,
          calculatorId,
          calculatorName,
        });
        const newTabId = tab.id;
        const nextTabs = bumpActivityPair([...prev.tabs, tab], newTabId, null);

        return {
          ...prev,
          counters,
          tabs: nextTabs,
          activeTabId: newTabId,
        };
      }

      // 6) No panes available, so fallback to fullscreen and minimize.
      const { tab, counters } = createTabInstance(type, prev.counters, "fullscreen", {
        projectId,
        projectName,
        clientName,
        isMiscProject,
        calculatorId,
        calculatorName,
      });

      const newTabId = tab.id;

      const minimizedVisible = prev.tabs.map((t) => {
        if (t.isMinimized) return t;
        return {
          ...t,
          isMinimized: true,
          preMinimizeTile: t.tilePosition,
        };
      });

      setMinimizedTabIds((prevSet) => {
        const s = new Set(prevSet);
        for (const t of minimizedVisible) {
          if (t.isMinimized) s.add(t.id);
        }
        return s;
      });

      const nextTabs = bumpActivityPair(
        [...minimizedVisible, tab],
        newTabId,
        null,
      );

      return {
        ...prev,
        counters,
        tabs: nextTabs,
        activeTabId: newTabId,
      };
    });
  },
  [getToolsBounds, setMinimizedTabIds, setTabMinimized, sidebarOffset],
);


  React.useEffect(() => {
    const handler = (event: Event) => {
      const custom = event as CustomEvent<{
        type?: TabType;
        forceNew?: boolean;
        projectId?: string | null;
        projectName?: string | null;
        clientName?: string | null;
        isMisc?: boolean;
        calculatorId?: string;
        calculatorName?: string;
      }>;
      if (!custom.detail?.type) return;
      openTab(custom.detail.type, {
        forceNew: custom.detail.forceNew,
        projectId: custom.detail.projectId,
        projectName: custom.detail.projectName,
        clientName: custom.detail.clientName,
        isMisc: custom.detail.isMisc,
        calculatorId: custom.detail.calculatorId,
        calculatorName: custom.detail.calculatorName,
      });
    };
    window.addEventListener("workspace:open-tab", handler as EventListener);
    return () =>
      window.removeEventListener("workspace:open-tab", handler as EventListener);
  }, [openTab]);

  const closeTab = React.useCallback(
    (tabId: string) => {
      setState((prev) => {
        const idx = tabById(prev, tabId);
        if (idx === -1) return prev;
        const tab = prev.tabs[idx];
        if (
          (tab.type === "notes" || tab.type === "tasks") &&
          tab.dirty &&
          typeof window !== "undefined" &&
          !window.confirm(
            "Unsaved data detected. Are you sure you want to close this tab?",
          )
        ) {
          return prev;
        }
        const nextTabs = prev.tabs.filter((t) => t.id !== tabId);
        let nextActive = prev.activeTabId;
        if (prev.activeTabId === tabId) {
          const remaining = nextTabs;
          if (remaining.length > 0) {
            let best = remaining[0];
            for (const t of remaining) {
              const tLast = t.lastActiveAt ?? 0;
              const bLast = best.lastActiveAt ?? 0;
              if (tLast > bLast) {
                best = t;
              }
            }
            nextActive = best.id;
          } else {
            nextActive = undefined;
          }
        }
        const nextMin = new Set(minimizedTabIds);
        nextMin.delete(tabId);
        setMinimizedTabIds(nextMin);
        const baseNext = { ...prev, tabs: nextTabs, activeTabId: nextActive };
        return baseNext;
      });
    },
    [minimizedTabIds],
  );

  const applyTabSnap = React.useCallback(
    (tabId: string, zone: Exclude<DropZone, "tab-bar">) => {
      if (isTabLocked(tabId)) return;

      setState((prev) => {
        const idx = tabById(prev, tabId);
        if (idx === -1) return prev;
        const bounds = getToolsBounds();
        if (!bounds) return prev;
        const tiles = computeTileRects(bounds, sidebarOffset);
        const desiredRect = tiles[zone];
        if (!desiredRect) return prev;

        let nextTabs = [...prev.tabs];

        nextTabs[idx] = {
          ...nextTabs[idx],
          tilePosition: zone,
          isMinimized: false,
        };

        const relocateZones: Exclude<DropZone, "tab-bar">[] = [
          "left-half",
          "right-half",
          "top-half",
          "bottom-half",
          "top-left",
          "top-right",
          "bottom-left",
          "bottom-right",
          "center",
        ];

        const displacedIds = new Set<string>();

        const visibleOthers = nextTabs.filter(
          (t, i) => !t.isMinimized && i !== idx,
        );

        for (const other of visibleOthers) {
          const otherRect = tiles[other.tilePosition];
          if (!otherRect || !rectsOverlap(otherRect, desiredRect)) continue;

          const stayingRects = nextTabs
            .filter(
              (t) =>
                !t.isMinimized &&
                t.id !== other.id &&
                !displacedIds.has(t.id),
            )
            .map((t) => tiles[t.tilePosition])
            .filter(Boolean) as Rect[];

          let relocated = false;
          for (const rZone of relocateZones) {
            const zoneRect = tiles[rZone];
            if (!zoneRect) continue;
            if (stayingRects.some((r) => rectsOverlap(zoneRect, r))) continue;
            nextTabs = nextTabs.map((t) =>
              t.id === other.id ? { ...t, tilePosition: rZone } : t,
            );
            relocated = true;
            break;
          }

          if (!relocated) {
            displacedIds.add(other.id);
            nextTabs = nextTabs.map((t) =>
              t.id === other.id
                ? { ...t, isMinimized: true, preMinimizeTile: other.tilePosition }
                : t,
            );
          }
        }

        setMinimizedTabIds((prevSet) => {
          const s = new Set(prevSet);
          for (const id of displacedIds) s.add(id);
          return s;
        });

        const bumpedTabs = bumpActivityPair(nextTabs, tabId, null);
        return { ...prev, tabs: bumpedTabs, activeTabId: tabId };
      });
    },
    [getToolsBounds, setMinimizedTabIds, sidebarOffset, isTabLocked],
  );

  const beginWindowDrag = React.useCallback(
    (tabId: string, point: { x: number; y: number }) => {

       // 🚫 BLOCK if locked
      if (isTabLocked(tabId)) return;
      const { dropZone, previewRect } = evaluateDropMeta(point);

      const next: WindowDragState = {
        isDragging: true,
        tabId,
        startPointer: point,
        pointer: point,
        dropZone,
        previewRect,
      };
      setState((prev) => ({ ...prev, windowDragState: next }));
    },
    [evaluateDropMeta, isTabLocked],
  );

  const updateWindowDrag = React.useCallback(
    (point: { x: number; y: number }) => {
      setState((prev) => {
        const current = prev.windowDragState;
        if (!current?.isDragging) return prev;
        const { dropZone, previewRect } = evaluateDropMeta(point);
        return {
          ...prev,
          windowDragState: {
            ...current,
            pointer: point,
            dropZone,
            previewRect,
          },
        };
      });
    },
    [evaluateDropMeta],
  );

  const endWindowDrag = React.useCallback(
    (opts?: { cancel?: boolean }) => {
      setState((prev) => {
        const current = prev.windowDragState;
        if (!current?.isDragging) return { ...prev, windowDragState: null };
        const nextState = { ...prev, windowDragState: null };
        if (
          !opts?.cancel &&
          current.dropZone &&
          current.dropZone !== "tab-bar"
        ) {
          setTimeout(
            () =>
              applyTabSnap(
                current.tabId,
                current.dropZone as Exclude<DropZone, "tab-bar">,
              ),
            0,
          );
        }
        return nextState;
      });
    },
    [applyTabSnap],
  );

  const toggleMaximize = React.useCallback(
    (tabId: string) => {
      setState((prev) => {
        const idx = tabById(prev, tabId);
        if (idx === -1) return prev;
        const t = prev.tabs[idx];

        if (t.tilePosition === "fullscreen") {
          // Un-maximize: restore this tab to its preMinimizeTile (or left-half),
          // and un-minimize any tabs that were displaced by the maximize.
          const restoreTile = t.preMinimizeTile ?? "left-half";

          const bounds = getToolsBounds();
          const tiles = bounds
            ? computeTileRects(bounds, sidebarOffset)
            : prev.tileRects;
          const restoredRect = tiles?.[restoreTile];

          let nextTabs = prev.tabs.map((tab, i) => {
            if (i === idx) {
              return { ...tab, tilePosition: restoreTile, preMinimizeTile: undefined };
            }
            if (tab.isMinimized && tab.preMinimizeTile) {
              return { ...tab, isMinimized: false, tilePosition: tab.preMinimizeTile, preMinimizeTile: undefined };
            }
            return tab;
          });

          // After restoring everyone, resolve any overlaps with the same
          // relocate-or-minimize logic.
          if (restoredRect && tiles) {
            const relocateZones: Exclude<DropZone, "tab-bar">[] = [
              "left-half", "right-half", "top-half", "bottom-half",
              "top-left", "top-right", "bottom-left", "bottom-right", "center",
            ];
            const displacedIds = new Set<string>();
            const allVisible = nextTabs.filter((tab) => !tab.isMinimized);

            for (let i2 = 0; i2 < allVisible.length; i2++) {
              for (let j = i2 + 1; j < allVisible.length; j++) {
                const a = allVisible[i2];
                const b = allVisible[j];
                const ra = tiles[a.tilePosition];
                const rb = tiles[b.tilePosition];
                if (!ra || !rb || !rectsOverlap(ra, rb)) continue;

                const victim =
                  (a.lastActiveAt ?? 0) < (b.lastActiveAt ?? 0) ? a : b;
                if (displacedIds.has(victim.id)) continue;

                const stayingRects = nextTabs
                  .filter(
                    (tab) =>
                      !tab.isMinimized &&
                      tab.id !== victim.id &&
                      !displacedIds.has(tab.id),
                  )
                  .map((tab) => tiles[tab.tilePosition])
                  .filter(Boolean) as Rect[];

                let relocated = false;
                for (const zone of relocateZones) {
                  const zr = tiles[zone];
                  if (!zr) continue;
                  if (stayingRects.some((r) => rectsOverlap(zr, r))) continue;
                  nextTabs = nextTabs.map((tab) =>
                    tab.id === victim.id ? { ...tab, tilePosition: zone } : tab,
                  );
                  relocated = true;
                  break;
                }

                if (!relocated) {
                  displacedIds.add(victim.id);
                  nextTabs = nextTabs.map((tab) =>
                    tab.id === victim.id
                      ? { ...tab, isMinimized: true, preMinimizeTile: victim.tilePosition }
                      : tab,
                  );
                }
              }
            }

            setMinimizedTabIds((prevSet) => {
              const s = new Set<string>();
              for (const tab of nextTabs) {
                if (tab.isMinimized) s.add(tab.id);
              }
              return s;
            });
          } else {
            setMinimizedTabIds((prevSet) => {
              const s = new Set<string>();
              for (const tab of nextTabs) {
                if (tab.isMinimized) s.add(tab.id);
              }
              return s;
            });
          }

          return { ...prev, tabs: nextTabs };
        }

        // Maximize: save current tile, minimize all others
        const nextTabs = prev.tabs.map((tab, i) => {
          if (i === idx) {
            return {
              ...tab,
              tilePosition: "fullscreen" as Exclude<DropZone, "tab-bar">,
              preMinimizeTile: tab.tilePosition,
              isMinimized: false,
            };
          }
          if (tab.isMinimized) return tab;
          return {
            ...tab,
            isMinimized: true,
            preMinimizeTile: tab.tilePosition,
          };
        });

        setMinimizedTabIds(() => {
          const s = new Set<string>();
          for (const tab of nextTabs) {
            if (tab.isMinimized) s.add(tab.id);
          }
          return s;
        });

        return {
          ...prev,
          tabs: nextTabs,
          activeTabId: tabId,
        };
      });
    },
    [getToolsBounds, sidebarOffset, setMinimizedTabIds],
  );

  const contextValue = React.useMemo(
    () => ({
      state,
      openTab,
      closeTab,
      setActiveTab,
      setTabTitle,
      setTabScope,
      markTabDirty,
      minimizedTabIds,
      isTabMinimized,
      setTabMinimized,
      beginWindowDrag,
      updateWindowDrag,
      endWindowDrag,
      applyTabSnap,
      toggleMaximize,
      isTabLocked,
      toggleTabLocked,
    }),
    [
      state,
      openTab,
      closeTab,
      setActiveTab,
      setTabTitle,
      setTabScope,
      markTabDirty,
      minimizedTabIds,
      isTabMinimized,
      setTabMinimized,
      beginWindowDrag,
      updateWindowDrag,
      endWindowDrag,
      applyTabSnap,
      toggleMaximize,
      isTabLocked,
      toggleTabLocked,
    ],
  );

  const layoutContent = React.useMemo(() => {
    const tiles = state.tileRects;
    return (
      <div className="relative flex min-h-0 flex-1">
        <div className="absolute inset-0 relative">
          {state.tabs
            .filter((t) => !t.isMinimized)
            .map((t) => {
              const rect = tiles[t.tilePosition];
              if (!rect) return null;

              return (
                <div
                  key={t.id}
                  className="absolute"
                  style={{
                    left: rect.x,
                    top: rect.y,
                    width: rect.width,
                    height: rect.height,
                    zIndex: t.id === state.activeTabId ? 10 : 1,
                  }}
                  onMouseDown={() => setActiveTab(t.id)}
                >
                  <WindowFrame
                    tab={t}
                    isMinimized={Boolean(t.isMinimized)}
                    isMinimizing={false}
                    isActive={t.id === state.activeTabId}
                    onMinimize={() => setTabMinimized(t.id, true)}
                    onClose={() => closeTab(t.id)}
                  >
                    <div className="h-full w-full overflow-auto">
                      <TabContent
                        tab={t}
                        markDirty={markTabDirty}
                        user={userHeaders}
                      />
                    </div>
                  </WindowFrame>
                </div>
              );
            })}
        </div>
      </div>
    );
  }, [
    state.tabs,
    state.tileRects,
    setTabMinimized,
    closeTab,
    markTabDirty,
    setActiveTab,
    userHeaders,
    state.activeTabId,
  ]);

  const windowDragOverlay = state.windowDragState?.isDragging ? (
    <div className="pointer-events-none absolute inset-0 z-50">
      {state.windowDragState.previewRect ? (
        <div
          className="absolute rounded-md border border-primary/60 bg-primary/15"
          style={{
            left: `${state.windowDragState.previewRect.x}px`,
            top: `${state.windowDragState.previewRect.y}px`,
            width: `${state.windowDragState.previewRect.width}px`,
            height: `${state.windowDragState.previewRect.height}px`,
          }}
        />
      ) : null}
    </div>
  ) : null;

  const [frameBounds, setFrameBounds] = React.useState<Record<string, Rect>>({});
  React.useEffect(() => {
    if (!debugFrames) return;
    const handler = (event: Event) => {
      const e = event as CustomEvent<{
        tabId: string;
        x: number;
        y: number;
        width: number;
        height: number;
      }>;
      const detail = e.detail;
      if (!detail) return;
      setFrameBounds((prev) => ({
        ...prev,
        [detail.tabId]: {
          x: detail.x,
          y: detail.y,
          width: detail.width,
          height: detail.height,
        },
      }));
    };
    window.addEventListener(
      "workspace:frame-bounds",
      handler as EventListener,
    );
    return () =>
      window.removeEventListener(
        "workspace:frame-bounds",
        handler as EventListener,
      );
  }, [debugFrames]);

  return (
    <WorkspaceContext.Provider value={contextValue}>
      <div ref={rootRef} className="relative flex min-h-0 flex-1 flex-col">
        <div
          ref={workspaceRef}
          data-tutorial="workspace"
          className="relative flex min-h-0 flex-1 flex-col overflow-hidden"
          onDragOver={(e) => {
            if (!state.windowDragState?.isDragging) return;
            e.preventDefault();
            if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
            updateWindowDrag({ x: e.clientX, y: e.clientY });
          }}
          onDrop={(e) => {
            if (!state.windowDragState?.isDragging) return;
            e.preventDefault();
            if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
            updateWindowDrag({ x: e.clientX, y: e.clientY });
            endWindowDrag();
          }}
        >
          <StarryBackground variant="workspace" showPrompts={true} />

          {/* {showWorkspaceLock && (
            <WorkspaceBlastShutter
              onRequestLogin={() => {
                // Hook this into Clerk later
                // Example:
                // openSignIn()
                console.log("Login requested");
              }}
            />
          )} */}

          <div className="absolute inset-0 z-0 pointer-events-auto" />
          <div className="relative z-10 flex min-h-0 flex-1 flex-col">
            {windowDragOverlay}
            {layoutContent}
          </div>
        </div>

        <BottomTabBar />

        {debugFrames ? (
          <div className="pointer-events-none fixed inset-0 z-[9999]">
            {workspaceBox ? (
              <div
                className="fixed border border-yellow-400/90"
                style={{
                  left: workspaceBox.x,
                  top: workspaceBox.y,
                  width: workspaceBox.width,
                  height: workspaceBox.height,
                }}
              />
            ) : null}
            {Object.entries(frameBounds).map(([id, rect]) => {
              if (!rect) return null;
              return (
                <div
                  key={`frame-${id}`}
                  className="fixed border-2 border-cyan-400/90 border-dashed"
                  style={{
                    left: rect.x,
                    top: rect.y,
                    width: rect.width,
                    height: rect.height,
                  }}
                />
              );
            })}
          </div>
        ) : null}
      </div>
    </WorkspaceContext.Provider>
  );
}

function TabContent({
  tab,
  markDirty,
  user,
}: {
  tab: WorkspaceTab;
  markDirty: (tabId: string, dirty: boolean) => void;
  user?: { id?: string; email?: string };
}) {
  const dirtyHandler = React.useCallback(
    (dirty: boolean) => markDirty(tab.id, dirty),
    [markDirty, tab.id],
  );

  switch (tab.type) {
    case "chat":
      return <ChatView tabId={tab.id} />;

    case "notes":
      return (
        <NotesPanel
          tabId={tab.id}
          user={user}
          onDirtyChange={dirtyHandler}
          projectId={tab.projectId}
          projectName={tab.projectName}
          isMiscProject={tab.isMiscProject}
        />
      );

    case "tasks":
      return (
        <TasksPanel
          tabId={tab.id}
          user={user}
          onDirtyChange={dirtyHandler}
          projectId={tab.projectId}
          projectName={tab.projectName}
          isMiscProject={tab.isMiscProject}
        />
      );

    case "custom":
      return <CustomCalculatorBuilder />;

    case "tracker":
      return (
        <ProjectTrackerPanel
          tabId={tab.id}
          projectId={tab.projectId}
          projectName={tab.projectName}
          clientName={tab.clientName}
        />
      );

    case "owner-requirements":
      return (
        <OwnerRequirementsPanel
          tabId={tab.id}
          projectId={tab.projectId}
          projectName={tab.projectName}
          clientName={tab.clientName}
        />
      );

    case "timesheet":
      return <TimesheetPanel tabId={tab.id} user={user} />;

    case "calcera-ball":
      return <CalceraBallPanel />;

    default:
      return <div>Unsupported tab.</div>;
  }
}
